import React, {createContext, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Button, Skeleton, Space} from 'antd';
import {useTranslation} from 'react-i18next';
import {useFacets} from '../../contexts/AggsContext';

const FacetFocusContext = createContext(() => {});
export const useRememberFacetFocus = () => useContext(FacetFocusContext);

export function FacetError({retry}) {
    const {t} = useTranslation();
    return <Space role="status">
        {t('facet-counts-failed', 'Could not load counts.')}
        <Button size="small" onClick={retry}>{t('retry', 'Retry')}</Button>
    </Space>;
}

export default function DeferredFacets({fields = [], labelledBy, children}) {
    const {t} = useTranslation();
    const {available, failed, retry} = useFacets(fields);
    const [mounted, setMounted] = useState(false);
    const root = useRef();
    const pendingFocus = useRef();
    const rememberFocus = element => {
        if(element === root.current || document.activeElement !== element) return;
        pendingFocus.current = {element, key: element.dataset.facetFocus, id: element.id,
            name: element.getAttribute('name'), value: element.getAttribute('value'),
            label: element.getAttribute('aria-label'), role: element.getAttribute('role')};
    };
    useEffect(() => {
        const moved = event => {
            if(event.target !== document.body && event.target !== pendingFocus.current?.element) pendingFocus.current = null;
        };
        document.addEventListener('focusin', moved);
        return () => document.removeEventListener('focusin', moved);
    }, []);
    useLayoutEffect(() => {
        if(!(available || failed) || !pendingFocus.current) return;
        const pending = pendingFocus.current;
        pendingFocus.current = null;
        if(document.activeElement !== document.body) return;
        const controls = [...(root.current?.querySelectorAll('button, input, select, textarea, [tabindex]') || [])];
        const replacement = failed ? root.current?.querySelector('button') :
            root.current?.contains(pending.element) ? pending.element : controls.find(element =>
                pending.key ? element.dataset.facetFocus === pending.key :
                pending.id ? element.id === pending.id :
                pending.name ? element.getAttribute('name') === pending.name && element.getAttribute('value') === pending.value :
                pending.label && element.getAttribute('aria-label') === pending.label && element.getAttribute('role') === pending.role);
        (replacement || root.current)?.focus();
    }, [available, failed]);
    useEffect(() => { if(available) setMounted(true); }, [available]);
    // Keep controls visible during same-search refreshes; a new search clears
    // availability, hiding obsolete counts while preserving local control state.
    return <div ref={root} tabIndex={-1} role="group" aria-labelledby={labelledBy} aria-label={labelledBy ? undefined : t('filter-options', 'Filter options')}
        onFocusCapture={event => rememberFocus(event.target)}>
        <FacetFocusContext.Provider value={rememberFocus}>
        {failed ? <FacetError retry={retry}/> : !available &&
            <div role="status" aria-label={t('loading-counts', 'Loading counts')}><Skeleton active paragraph={{rows: 2}} title={false}/></div>}
        <div hidden={!available}>{(mounted || available) && children}</div>
        </FacetFocusContext.Provider>
    </div>;
}
